















































































































import {Component, Prop, Vue} from "vue-property-decorator";
import {perRoadStore} from "@/store/store";
import {UnitSystem} from "@/interfaces/units";
import PxNumberInput from "@/views/components/input/formFields/PxNumberInput.vue";
import {
  ILayerPerformanceCriteriaImperial,
  ILayerPerformanceCriteriaMetric,
  IPerRoadLayerImperial,
} from "@/modules/perRoad/interfaces/perRoad";
import PrHorizontalStrainMeDesignPlot
  from "@/modules/perRoad/views/components/criteria/plots/PrHorizontalStrainMeDesignPlot.vue";
import {IPrMonteCarloDrawImperial,} from "@/modules/perRoad/interfaces/monteCarlo";
import {evaluateTransferFunction, filteredHorizStrain} from "@/modules/perRoad/lib/calculations/meDesignHorizontal";
import {getHorizontalStrainValuesFromWesleaResults} from "@/modules/perRoad/lib/calculations/horizontalStrainDistribution";


const echarts = require("echarts");

@Component({
  components: {
    PrHorizontalStrainMeDesignPlot,
    PxNumberInput,
  },
})
export default class ConventionalMeDesignHorizontal extends Vue {
  @Prop() performanceCriteria!: ILayerPerformanceCriteriaImperial | ILayerPerformanceCriteriaMetric;

  @Prop() id!: string;

  @Prop({default: 0}) externalKey!: number;

  @Prop() layerImperial!: IPerRoadLayerImperial;

  @Prop() layerNumber!: number;

  @Prop() draws!: IPrMonteCarloDrawImperial[];

  @Prop() aadt!: number;

  @Prop() pctTrucks!: number;

  @Prop() pctTruckGrowth!: number;

  @Prop() pctTrucksInDesignLane!: number;

  @Prop() directionalDistribution!: number;

  @Prop({default: false}) showResults!: boolean;

  internalKey = 0;

  get plotKey() {
    // console.log('updating key');
    return this.internalKey + this.externalKey;
  }

  get unitSystemIsImperial() {
    return perRoadStore.displayUnits === UnitSystem.Imperial;
  }

  get strainUnits() {
    return this.unitSystemIsImperial ? "x 10<sup>-6</sup>&nbsp&nbspin/in" : "x 10<sup>-6</sup>&nbsp&nbspcm/cm";
  }

  get transferFunctionNf() {
    let Nf = 0;
    if (this.performanceCriteria.useTransferFunction) {
      const k1 = this.performanceCriteria.k1 || 0;
      const k2 = this.performanceCriteria.k2 || 0;
      const eps_t = this.performanceCriteria.strainThreshold_ustrain || 0;

      Nf = k1 * (1 / (eps_t * 10 ** (-6))) ** k2;
    }
    return Nf;
  }


  sync() {
    this.$emit("sync");
    this.internalKey += 1;
  }

  get horizontalStrainValues() {
    /**
     * It's necessary to perform an operation in this getter that involves this.externalKey
     * in order to trigger recalculation & display after an update from the simulation.
     *
     * Looks like dead code but it's not!
     */
    const necessaryUnusedVariable = this.externalKey;

    this.internalKey += 1;

    // Don't plot results if we don't have good numbers to show
    if (!this.showResults) {
      return [];
    }

    return getHorizontalStrainValuesFromWesleaResults(
        this.draws,
        this.layerNumber,
        this.performanceCriteria.location,
    ).filter((v) => v !== 0 && v !== Infinity);
  }

  get actualPercentileBelowThreshold() {
    const sortedValues = this.horizontalStrainValues.sort((a, b) => b - a);
    const cumulativePct = sortedValues.map((v, i) => i / sortedValues.length);

    const data = sortedValues.map((v, i) => [v * 1000000, (1 - cumulativePct[i]) * 100]);

    const pctileIdx = data.findIndex((v) => v[0] < (this.performanceCriteria.strainThreshold_ustrain || 0));
    // data.forEach((v) => console.debug(v[0], v[1], v[0] < (this.performanceCriteria.strainThreshold_ustrain || 0)));
    if (data[pctileIdx]) {
      return data[pctileIdx][1];
    }
    return 0;
  }


  get meetsThresholdTest() {
    if (this.actualPercentileBelowThreshold === -1) {
      return false;
    }
    return this.actualPercentileBelowThreshold > (this.performanceCriteria.targetPercentile || 0);
  }

  get dBar() {
    /**
     * dBar is the average damage per truck. It's the input to miner's hypothesis used to calculate time to failure.
     */
    const k1 = this.performanceCriteria.k1 || 0;
    const k2 = this.performanceCriteria.k2 || 0;

    const nfs = filteredHorizStrain(this.horizontalStrainValues, this.performanceCriteria.strainThreshold_ustrain || 0)
        .map((strain) => (strain > 0 ? evaluateTransferFunction(k1, k2, strain) : null));

    const Ds = nfs.map((nf) => (nf ? 1 / nf : 0));
    const dbar = Ds.reduce((a, b) => a + b, 0) / Ds.length;

    return dbar;
  }

  get trucksPerYear() {
    const years = [];
    for (let i = 0; i < 50; i += 1) {
      years.push(i);
    }
    // Get number of trucks for each year

    const {aadt} = this;
    const truckFraction = this.pctTrucks / 100;
    const dirDist = this.directionalDistribution / 100;
    const pctGrowth = this.pctTruckGrowth / 100;

    const nTrucks = years.map((y) => aadt * 365.25 * truckFraction * dirDist * (1 + pctGrowth * y));
    return nTrucks;
  }

  get yearsUntilFailure() {
    const totalDmgEachYear = this.trucksPerYear.map((n) => n * this.dBar);

    // @ts-ignore
    const cumSum = ((sum) => (value) => sum += value)(0);
    const cumDmg = totalDmgEachYear.map(cumSum);
    // console.log(cumDmg);
    return cumDmg.findIndex((x) => x > 1);
  }
}
